import createClient, { type Middleware } from 'openapi-fetch'
import type { paths } from './schema'
import { Sentry } from '../lib/sentry'
import { BILLING_REQUIRED_EVENT } from '../lib/billing-events'

/** Holds the getToken function from Clerk's useAuth hook */
export const tokenStore = {
  token: '',
  getToken: null as (() => Promise<string | null>) | null,
}

// A pristine clone of each request, kept so a 401 retry can re-send the body.
// The request handed to onResponse has already had its body consumed by the
// first fetch, so retrying it directly throws "Request body is already used".
const retryClones = new WeakMap<Request, Request>()

// Shared post-processing applied to every response the middleware returns,
// including the response from a 401-refresh retry — a 402 (or other error)
// surfaced only on retry must still dispatch the billing event / breadcrumb.
function notice(response: Response, request: Request): Response {
  if (response.status === 402) {
    window.dispatchEvent(new CustomEvent(BILLING_REQUIRED_EVENT))
  }

  // Sentry breadcrumb for API errors (4xx/5xx)
  if (response.status >= 400) {
    Sentry.addBreadcrumb({
      category: 'api',
      message: `${request.method} ${new URL(request.url).pathname} → ${response.status}`,
      level: response.status >= 500 ? 'error' : 'warning',
      data: {
        status: response.status,
        request_id: response.headers.get('X-Request-Id') ?? undefined,
      },
    })
  }

  return response
}

export const authMiddleware: Middleware = {
  async onRequest({ request }) {
    // Prefer fresh token from Clerk (cached internally, no extra API call)
    if (tokenStore.getToken) {
      const token = await tokenStore.getToken()
      if (token) {
        tokenStore.token = token
        request.headers.set('Authorization', `Bearer ${token}`)
      }
    } else if (tokenStore.token) {
      request.headers.set('Authorization', `Bearer ${tokenStore.token}`)
    }
    // Clone before the body is read so a retry has an unconsumed copy.
    retryClones.set(request, request.clone())
    return request
  },
  async onResponse({ response, request }) {
    const clone = retryClones.get(request)
    retryClones.delete(request)
    // On 401, refresh token and retry once with the pristine clone. The
    // retried response gets the same post-processing (402 event + Sentry
    // breadcrumb) as any other response before it's returned.
    if (response.status === 401 && tokenStore.getToken && clone) {
      const freshToken = await tokenStore.getToken()
      if (freshToken && freshToken !== tokenStore.token) {
        tokenStore.token = freshToken
        clone.headers.set('Authorization', `Bearer ${freshToken}`)
        const retried = await fetch(clone)
        return notice(retried, request)
      }
    }

    return notice(response, request)
  },
}

export const client = createClient<paths>({
  baseUrl: '/',
  // Late-bind to globalThis.fetch instead of letting openapi-fetch capture it
  // as a default-parameter value at module-eval time. Without this, tests that
  // call vi.stubGlobal('fetch', ...) inside a test body have no effect, because
  // this module (and its captured fetch reference) already evaluated on import.
  fetch: (...args: Parameters<typeof fetch>) => globalThis.fetch(...args),
})

client.use(authMiddleware)
